import React, { useState } from 'react';
import {
  IconButton,
  Button,
  Popover,
  Box,
  Typography,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import { DataObject as VariableIcon } from '@mui/icons-material';
import { getAllFieldsAcrossScreens, buildVariableReference } from '../../utils/conditionUtils';

/**
 * A popover that lets users browse and select variable references to insert
 * into text or expression fields.
 *
 * Props:
 *   screens         – all screens in the flow
 *   currentScreenId – ID of the screen being edited
 *   onInsert        – called with the variable reference string (e.g. "${form.name}")
 *   label           – when set, renders a prominent labelled button instead of an icon-only
 *                     trigger (use where you want to nudge users to click rather than type)
 */
const VariablePicker = ({ screens, currentScreenId, onInsert, label }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const fieldGroups = getAllFieldsAcrossScreens(screens || [], currentScreenId);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (screenId, fieldName, isCurrent) => {
    onInsert(buildVariableReference(screenId, fieldName, isCurrent));
    handleClose();
  };

  // What we SHOW the user. Current-screen fields use the simple "${form.field}" form.
  // Cross-screen fields would otherwise expose a raw screen id, so display the screen
  // title instead. The value actually inserted still comes from buildVariableReference.
  const displayReference = (group, fieldName) =>
    group.isCurrent
      ? `\${form.${fieldName}}`
      : `\${${group.screenTitle}.form.${fieldName}}`;

  const open = Boolean(anchorEl);

  return (
    <>
      {label ? (
        <Button
          size="small"
          variant="outlined"
          onClick={handleOpen}
          startIcon={<VariableIcon fontSize="small" />}
          sx={{ textTransform: 'none' }}
        >
          {label}
        </Button>
      ) : (
        <Tooltip title="Insert variable reference">
          <IconButton
            size="small"
            onClick={handleOpen}
            sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <VariableIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 240, maxWidth: 320, maxHeight: 320, overflow: 'auto' } }}
      >
        <Box sx={{ p: 1.5, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Insert Variable
          </Typography>
        </Box>

        {fieldGroups.length === 0 && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              No input fields available yet. Add TextInput, RadioButtonsGroup, Dropdown, or similar components first.
            </Typography>
          </Box>
        )}

        {fieldGroups.map((group, groupIdx) => (
          <Box key={group.screenId}>
            {groupIdx > 0 && <Divider />}
            <Box sx={{ px: 2, pt: 1, pb: 0.25 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {group.isCurrent ? 'This screen' : group.screenTitle}
              </Typography>
            </Box>
            {group.fields.map(field => (
              <MenuItem
                key={field.fieldName}
                dense
                onClick={() => handleSelect(group.screenId, field.fieldName, group.isCurrent)}
                sx={{ py: 0.5 }}
              >
                <Box>
                  <Typography variant="body2">{field.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {displayReference(group, field.fieldName)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Box>
        ))}
      </Popover>
    </>
  );
};

export default VariablePicker;
